import { DataEditContextTextField } from 'Components/reusable/DataContext/fields/DataEditContextTextField'
import { DataEditContextDatePickerField } from 'Components/reusable/DataContext/fields/DataEditContextDatePickerField'
import Grid from '@mui/material/Grid2'
import { useSearchData } from 'Components/General/SearchProvider/SearchProvider'
import React, { useMemo } from 'react'
import {
  ISearchAvailability,
  ISearchCertificate,
  ISearchCompetence,
  ISearchCourse,
  ISearchDegree,
  ISearchFields,
  ISearchLanguage,
  ISearchSkill,
} from 'types/searchInterfaces'
import { useNotificationPopup } from 'Components/reusable/Notification'
import { IIndustry, IRole, ISkill } from 'types/levelItemInterfaces'
import LanguageSearchRequirement from 'Components/General/LanguageSearchRequirement/LanguageSearchRequirement'
import SkillSearchRequirement from 'Components/General/SkillSearchRequirement/SkillSearchRequirement'
import { useTranslation } from 'react-i18next'
import EducationSearchRequirement from './EducationSearchRequirement'
import { ICertificate, ICompetence } from 'types/cvsInterfaces'
import { useUser } from 'hooks'

/** @notExported */
interface ISearchFieldsProps {
  /** Skill options */
  skillOptions: ISkill[]
  /** Role options */
  roleOptions: IRole[]
  /** Industry options */
  industryOptions: IIndustry[]
  /** Certificate options */
  certificateOptions: ICertificate[]
  /** Competence options */
  competenceOptions: ICompetence[]
}

/**
 * SearchFields component for handling search fields.
 *
 * @param {Object} skillOptions - Options for skills
 * @param {Object} roleOptions - Options for roles
 * @param {Object} industryOptions - Options for industries
 */
const SearchFields: React.FC<ISearchFieldsProps> = ({
  skillOptions,
  roleOptions,
  industryOptions,
  certificateOptions,
  competenceOptions,
}) => {
  const { t } = useTranslation()
  const { searchData, setSearchData } = useSearchData()
  const { setNotificationPopup } = useNotificationPopup()
  const { features } = useUser()

  const anonymous = useMemo(() => {
    const networks = searchData.terms.networks
    return !!networks.find(network => network.anonymouslySearchable === true)
  }, [searchData.terms.networks])

  /**
   * Adds a skill, role, or industry to the searchData based on the provided type.
   *
   * @param {ISearchSkill | undefined} newItem - The item to be added
   * @param {'skill' | 'role' | 'industry'} type - The type of the item to be added
   * @return {void}
   */
  const addSkill = (
    newItem: ISearchSkill | ISearchCertificate | ISearchCompetence | undefined,
    type: 'skill' | 'role' | 'industry' | 'certificate' | 'competence'
  ): void => {
    if (!newItem) {
      return
    }
    if ('skillId' in newItem) {
      if (type === 'skill') {
        if (searchData.terms.skills.find(skill => skill.skillId === newItem.skillId)) {
          setNotificationPopup({
            message: t('search.skillAlreadyExists'),
            type: 'warning',
            duration: 'short',
          })
          return
        }

        if (newItem.name && newItem.name.length > 0) {
          setSearchData({
            ...searchData,
            terms: {
              ...searchData.terms,
              skills: [...searchData.terms.skills, newItem],
            },
          })
        }
      } else if (type === 'industry') {
        if (searchData.terms.industries.find(industry => industry.skillId === newItem.skillId)) {
          setNotificationPopup({
            message: t('search.industryAlreadyExists'),
            type: 'warning',
            duration: 'short',
          })
          return
        }

        if (newItem.name.length > 0) {
          setSearchData({
            ...searchData,
            terms: {
              ...searchData.terms,
              industries: [...searchData.terms.industries, newItem],
            },
          })
        }
      } else if (type === 'role') {
        if (searchData.terms.roles.find(role => role.skillId === newItem.skillId)) {
          setNotificationPopup({
            message: t('search.roleAlreadyExists'),
            type: 'warning',
            duration: 'short',
          })
          return
        }

        if (newItem.name.length > 0) {
          setSearchData({
            ...searchData,
            terms: {
              ...searchData.terms,
              roles: [...searchData.terms.roles, newItem],
            },
          })
        }
      }
    } else if ('certificateId' in newItem) {
      if (searchData.terms.certificates.find(certificate => certificate.certificateId === newItem.certificateId)) {
        setNotificationPopup({
          message: t('search.certificateAlreadyExists'),
          type: 'warning',
          duration: 'short',
        })
        return
      }

      if (newItem.certificate.length > 0) {
        setSearchData({
          ...searchData,
          terms: {
            ...searchData.terms,
            certificates: [...searchData.terms.certificates, newItem],
          },
        })
      }
    } else if ('competenceId' in newItem && features.includes('competences')) {
      if (searchData.terms.competences.find(competence => competence.competenceId === newItem.competenceId)) {
        setNotificationPopup({
          message: t('search.competenceAlreadyExists'),
          type: 'warning',
          duration: 'short',
        })
        return
      }

      if (newItem.name.length > 0) {
        setSearchData({
          ...searchData,
          terms: {
            ...searchData.terms,
            competences: [...searchData.terms.competences, newItem],
          },
        })
      }
    }
  }

  /**
   * Adds a new language to the search data if it does not already exist,
   * and sets a notification if the language already exists.
   *
   * @param {ISearchLanguage} newLanguage - the new language to be added
   */
  const addLanguage = (newLanguage?: ISearchLanguage) => {
    if (!newLanguage) return

    const languageExists = searchData.terms.languages.find(
      ({ languageCode }) => languageCode === newLanguage.languageCode
    )
    if (languageExists) {
      setNotificationPopup({
        message: t('search.languageAlreadyExists'),
        type: 'warning',
        duration: 'short',
      })
      return
    }

    const updatedLanguages = [
      ...searchData.terms.languages,
      { languageCode: newLanguage.languageCode, level: newLanguage.level, name: newLanguage.name },
    ]
    setSearchData({
      ...searchData,
      terms: {
        ...searchData.terms,
        languages: updatedLanguages,
      },
    })
  }

  /**
   * Adds education item to the searchData state if it doesn't already exist.
   *
   * @param {'degree' | 'course'} type - the type of education item
   * @param {ISearchDegree | ISearchCourse} newItem - the new education item to be added
   * @return {void}
   */
  const addEducation = (type: 'degree' | 'course', newItem: ISearchDegree | ISearchCourse): void => {
    if (!newItem) {
      return
    }

    if (type === 'degree') {
      const item = newItem as ISearchDegree
      const existingItem = searchData.terms.degrees.find(degree => degree.degree === item.degree)

      if (existingItem) {
        setNotificationPopup({
          message: t('search.degreeAlreadyExists'),
          type: 'warning',
          duration: 'short',
        })
        return
      }

      setSearchData({
        ...searchData,
        terms: {
          ...searchData.terms,
          degrees: [...searchData.terms.degrees, item],
        },
      })
    } else {
      const item = newItem as ISearchCourse
      const existingItem = searchData.terms.courses.find(course => course.course === item.course)

      if (existingItem) {
        setNotificationPopup({
          message: t('search.courseAlreadyExists'),
          type: 'warning',
          duration: 'short',
        })
        return
      }

      setSearchData({
        ...searchData,
        terms: {
          ...searchData.terms,
          courses: [...searchData.terms.courses, item],
        },
      })
    }
  }

  return (
    <>
      <Grid container spacing={1} alignItems="center" size={12}>
        <Grid sx={{ fontWeight: 'bold' }} size="auto">
          {t('search.availability')}:
        </Grid>
        <Grid size="auto">
          <DataEditContextDatePickerField<ISearchAvailability>
            field="startDate"
            dateResolution="month"
            isClearable
            start={true}
          />
        </Grid>
        <Grid>-</Grid>
        <Grid size={4}>
          <DataEditContextDatePickerField<ISearchAvailability>
            field="endDate"
            isClearable
            dateResolution="month"
            end={true}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1} size={12}>
        {!anonymous && (
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <DataEditContextTextField<ISearchFields> label={t('search.name')} field="name" insetLabel fullWidth />
          </Grid>
        )}
        <Grid
          size={{
            xs: 12,
            sm: 4,
          }}
        >
          <DataEditContextTextField<ISearchFields> label={t('search.location')} field="location" insetLabel fullWidth />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 4,
          }}
        >
          <LanguageSearchRequirement onAdd={newItem => addLanguage(newItem)} insetLabel />
        </Grid>
      </Grid>
      <Grid container spacing={1} size={12}>
        <Grid
          size={{
            xs: 12,
            sm: 4,
          }}
        >
          <SkillSearchRequirement
            onAdd={newItem => addSkill(newItem, 'skill')}
            type="skill"
            showUnpromoted={true}
            options={skillOptions}
            insetLabel
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 4,
          }}
        >
          <SkillSearchRequirement
            onAdd={newItem => addSkill(newItem, 'role')}
            type="role"
            showUnpromoted={true}
            options={roleOptions}
            insetLabel
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 4,
          }}
        >
          <SkillSearchRequirement
            onAdd={newItem => addSkill(newItem, 'industry')}
            type="industry"
            showUnpromoted={true}
            options={industryOptions}
            insetLabel
          />
        </Grid>
      </Grid>
      <Grid container spacing={1} size={12}>
        {certificateOptions && certificateOptions.length > 0 && (
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <SkillSearchRequirement
              onAdd={newItem => addSkill(newItem, 'certificate')}
              type="certificate"
              options={certificateOptions}
              insetLabel
            />
          </Grid>
        )}
        {features.includes('competences') && competenceOptions && competenceOptions.length > 0 && (
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <SkillSearchRequirement
              onAdd={newItem => addSkill(newItem, 'competence')}
              type="competence"
              options={competenceOptions}
              insetLabel
            />
          </Grid>
        )}
        <Grid
          size={{
            xs: 12,
            sm: 4,
          }}
        >
          <EducationSearchRequirement
            onAdd={newItem => addEducation('degree', newItem)}
            type="degree"
            noButton
            insetLabel
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 4,
          }}
        >
          <EducationSearchRequirement
            onAdd={newItem => addEducation('course', newItem)}
            type="course"
            noButton
            insetLabel
          />
        </Grid>
      </Grid>
    </>
  )
}

export default SearchFields
